
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

type Props = {
  title: string;
  value: string;
  allowDecimal?: boolean;
  preserveLeadingZeros?: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
};

const NUMBER_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export default function ProductionOperatorInlineNumericKeyboard({
  title,
  value,
  allowDecimal = false,
  preserveLeadingZeros = false,
  onChange,
  onClose,
}: Props) {
  const appendDigit = (digit: string) => {
    if (!preserveLeadingZeros && value === '0') {
      onChange(digit);
      return;
    }

    onChange(`${value}${digit}`);
  };

  const appendDecimal = () => {
    if (!allowDecimal || value.includes('.') || value.includes(',')) return;
    onChange(value ? `${value}.` : '0.');
  };

  const keySx = {
    minHeight: 54,
    borderRadius: 2,
    borderColor: 'rgba(148,163,184,0.22)',
    bgcolor: 'rgba(30,41,59,0.72)',
    color: '#f8fafc',
    fontSize: '1.3rem',
    fontWeight: 900,
    '&:hover': {
      bgcolor: 'rgba(51,65,85,0.9)',
      borderColor: '#38bdf8',
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(56,189,248,0.3)',
        bgcolor: '#0f172a',
        p: 1.5,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Teclado PROA · {title}
          </Typography>

          <Button size="small" onClick={onClose}>
            Terminar
          </Button>
        </Stack>

        <Box
          sx={{
            borderRadius: 2,
            bgcolor: 'rgba(2,6,23,0.6)',
            border: '1px solid rgba(56,189,248,0.2)',
            px: 1.5,
            py: 1,
            textAlign: 'right',
          }}
        >
          <Typography
            sx={{
              color: '#f8fafc',
              fontSize: '1.5rem',
              fontWeight: 950,
              overflowWrap: 'anywhere',
            }}
          >
            {value || '—'}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 0.75,
          }}
        >
          {NUMBER_KEYS.map((digit) => (
            <Button
              key={digit}
              variant="outlined"
              onClick={() => appendDigit(digit)}
              sx={keySx}
            >
              {digit}
            </Button>
          ))}

          <Button
            variant="outlined"
            disabled={!allowDecimal}
            onClick={appendDecimal}
            sx={keySx}
          >
            ,
          </Button>

          <Button
            variant="outlined"
            onClick={() => appendDigit('0')}
            sx={keySx}
          >
            0
          </Button>

          <Button
            variant="outlined"
            aria-label="Borrar último carácter"
            onClick={() => onChange(value.slice(0, -1))}
            sx={keySx}
          >
            <BackspaceRoundedIcon />
          </Button>
        </Box>

        <Button
          size="small"
          onClick={() => onChange('')}
          sx={{ color: '#94a3b8', fontWeight: 800 }}
        >
          Borrar todo
        </Button>
      </Stack>
    </Paper>
  );
}
